import React, { useEffect, useMemo } from "react";
import classNames from "classnames";
import { resolveElitVehicleId } from "../elitVehicleSelection";
import { DEFAULT_ORDER_FORM_LOCALE } from "../orderFormLocale";
import { ORDER_VEHICLES } from "../vehicleData";
import { OrderFormLocale } from "../types";
import ElitVehicleCard, { ElitVehicleCardLabels } from "./ElitVehicleCard";
import styles from "./ElitVehiclePanel.module.scss";

type ElitVehiclePanelProps = {
  isOpen: boolean;
  onClose: () => void;
  vehicleId: string;
  locale?: OrderFormLocale;
  onVehicleChange: (vehicleId: string) => void;
  onConfirm: () => void;
};

const ElitVehiclePanel: React.FC<ElitVehiclePanelProps> = ({
  isOpen,
  onClose,
  vehicleId,
  locale,
  onVehicleChange,
  onConfirm,
}) => {
  const resolvedLocale = locale ?? DEFAULT_ORDER_FORM_LOCALE;
  const elitLabels = resolvedLocale.elit ?? DEFAULT_ORDER_FORM_LOCALE.elit;
  const selectedVehicleId = resolveElitVehicleId(vehicleId);

  const panelSubtitle = (elitLabels?.vehiclePanelSubtitle ?? "{count} vehicles available").replace(
    "{count}",
    String(ORDER_VEHICLES.length)
  );

  const cardLabels: ElitVehicleCardLabels = useMemo(
    () => ({
      luggageLabel: elitLabels?.luggageCountLabel ?? "Luggage",
      passengersLabel: elitLabels?.passengersCountLabel ?? "Passengers",
      transferLabel: elitLabels?.transferLabel ?? "Transfer",
      hourlyRateLabel: elitLabels?.hourlyRateLabel ?? "Po satu",
      dailyRateLabel: elitLabels?.dailyRateLabel ?? "Dnevni angažman (8h)",
      pricePerKmLabel: elitLabels?.pricePerKmLabel ?? "Cena po km",
      minHoursLabel: resolvedLocale.minHours,
    }),
    [elitLabels, resolvedLocale.minHours]
  );

  useEffect(() => {
    if (!isOpen) return undefined;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [isOpen, onClose]);

  return (
    <div className={classNames(styles.root, { [styles.rootOpen]: isOpen })} aria-hidden={!isOpen}>
      <div
        className={classNames(styles.backdrop, { [styles.backdropVisible]: isOpen })}
        onClick={onClose}
        aria-hidden={!isOpen}
      />
      <aside
        className={classNames(styles.panel, { [styles.panelOpen]: isOpen })}
        role="dialog"
        aria-modal="true"
        aria-label={elitLabels?.vehiclePanelTitle ?? "Choose vehicle"}
        aria-hidden={!isOpen}
      >
        <header className={styles.panelHeader}>
          <h2 className={styles.panelTitle}>{elitLabels?.vehiclePanelTitle ?? "Choose vehicle"}</h2>
          <p className={styles.panelSubtitle}>{panelSubtitle}</p>
        </header>

        <div className={styles.list}>
          {ORDER_VEHICLES.map((vehicle) => (
            <ElitVehicleCard
              key={vehicle.id}
              vehicle={vehicle}
              selected={selectedVehicleId === vehicle.id}
              labels={cardLabels}
              onSelect={() => onVehicleChange(vehicle.id)}
            />
          ))}
        </div>

        <footer className={styles.footer}>
          <button type="button" className={styles.chooseBtn} onClick={onConfirm}>
            {elitLabels?.chooseCarLabel ?? "Choose car"}
          </button>
        </footer>
      </aside>
    </div>
  );
};

export default ElitVehiclePanel;
